import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/api/client";
import { applyTheme } from "@/lib/theme";

const BUSINESS_KEY = "bj_selected_business";
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessId, setSelectedBusinessIdState] = useState(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const me = await api.me();
      setUser(me);
    } catch {
      setUser(null);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const savedBiz = localStorage.getItem(BUSINESS_KEY);
      if (savedBiz) {
        setSelectedBusinessIdState(savedBiz);
        applyTheme(savedBiz);
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const setSelectedBusinessId = useCallback((id) => {
    setSelectedBusinessIdState(id);
    if (id) {
      localStorage.setItem(BUSINESS_KEY, id);
      applyTheme(id);
    } else {
      localStorage.removeItem(BUSINESS_KEY);
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    const r = await api.login({ email, password });
    setToken(r.access_token);
    setUser(r.user);
    return r.user;
  }, []);

  const signUp = useCallback(async (body) => {
    const r = await api.signup(body);
    setToken(r.access_token);
    setUser(r.user);
    return r.user;
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // Apply an auth result obtained out-of-band (e.g. Google OAuth callback).
  const applyAuth = useCallback((token, u) => {
    setToken(token);
    setUser(u);
  }, []);

  return (
    <AuthCtx.Provider
      value={{ user, loading, selectedBusinessId, setSelectedBusinessId, signIn, signUp, signOut, applyAuth, refresh }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
